import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Progress } from '@/components/ui/progress';
import { Search, Download, ChevronLeft, ChevronRight, ArrowLeft } from 'lucide-react';

function UserDetail({ userId, onBack }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/admin/users/${userId}`).then(res => setUser(res.data)).catch(console.error).finally(() => setLoading(false));
  }, [userId]);

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#F5C518] border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <p className="text-gray-400 text-center py-10">User not found</p>;

  return (
    <div className="animate-fade-in" data-testid="user-detail">
      <button onClick={onBack} className="flex items-center gap-1 text-gray-400 hover:text-[#D4A910] text-sm mb-4 transition-colors" data-testid="back-to-users">
        <ArrowLeft className="w-4 h-4" /> Back to Users
      </button>
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-[#F5C518]/20 flex items-center justify-center">
            <span className="text-[#D4A910] font-bold">{user.email?.[0]?.toUpperCase()}</span>
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">{user.email}</p>
            <p className="text-xs text-gray-400">Joined {user.created_at?.split('T')[0]}</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div><p className="text-lg font-bold text-[#D4A910]">{user.total_completed || 0}</p><p className="text-xs text-gray-400">Completed</p></div>
          <div><p className="text-lg font-bold text-gray-900">{user.login_count || 1}</p><p className="text-xs text-gray-400">Logins</p></div>
          <div><p className="text-lg font-bold text-gray-500">{user.last_active_at?.split('T')[0] || 'N/A'}</p><p className="text-xs text-gray-400">Last Active</p></div>
        </div>
      </div>
      {/* Chapter progress */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Chapter Progress</h3>
        <div className="space-y-3">
          {user.chapter_progress?.map(ch => (
            <div key={ch.chapter_number} className="flex items-center gap-3">
              <span className="w-6 h-6 rounded bg-[#F5C518]/10 flex items-center justify-center text-xs font-bold text-[#D4A910]">{ch.chapter_number}</span>
              <span className="text-xs text-gray-500 w-40 truncate">{ch.chapter_name}</span>
              <div className="flex-1 gold-progress"><Progress value={ch.total > 0 ? ch.completed / ch.total * 100 : 0} className="h-2 bg-gray-100" /></div>
              <span className="text-xs text-gray-500 w-10 text-right">{ch.completed}/{ch.total}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Activity */}
      {user.activity?.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Completion Timeline</h3>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {user.activity.map((a, i) => (
              <div key={i} className="flex items-center gap-3 py-1.5">
                <div className="w-2 h-2 rounded-full bg-green-500 shrink-0" />
                <span className="text-xs text-gray-700 flex-1 truncate">{a.title}</span>
                <span className="text-[10px] text-gray-400 shrink-0">{a.completed_at?.split('T')[0]}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/users', { params: { q: search, page, page_size: 20 } });
      setUsers(res.data.users);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [search, page]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleExport = async () => {
    try {
      const res = await api.get('/admin/users/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'users_export.csv'; a.click();
    } catch (err) { console.error('Export failed:', err); }
  };

  if (selectedUser) return <UserDetail userId={selectedUser} onBack={() => setSelectedUser(null)} />;

  return (
    <div className="animate-fade-in" data-testid="admin-users">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900">Users <span className="text-sm font-normal text-gray-400">({total})</span></h2>
        <button onClick={handleExport} className="px-3 py-1.5 text-xs font-medium text-[#D4A910] bg-[#F5C518]/10 hover:bg-[#F5C518]/20 border border-[#F5C518]/20 rounded-lg transition-colors flex items-center gap-1.5" data-testid="export-csv-button">
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input type="text" placeholder="Search by email..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 placeholder:text-gray-300 focus:outline-none focus:border-[#F5C518]"
          data-testid="user-search-input"
        />
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider hidden sm:table-cell">Registered</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider hidden md:table-cell">Last Active</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Progress</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="4" className="px-4 py-10 text-center"><div className="w-6 h-6 border-2 border-[#F5C518] border-t-transparent rounded-full animate-spin mx-auto" /></td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan="4" className="px-4 py-10 text-center text-gray-400">No users found</td></tr>
              ) : users.map(u => (
                <tr key={u.id} onClick={() => setSelectedUser(u.id)} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-[#F5C518]/10 flex items-center justify-center shrink-0">
                        <span className="text-[#D4A910] text-xs font-bold">{u.email?.[0]?.toUpperCase()}</span>
                      </div>
                      <span className="text-gray-900 truncate max-w-[200px]">{u.email}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-500 hidden sm:table-cell">{u.created_at?.split('T')[0]}</td>
                  <td className="px-4 py-3 text-gray-500 hidden md:table-cell">{u.last_active_at?.split('T')[0]}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-16 gold-progress"><Progress value={u.completion_pct || 0} className="h-1.5 bg-gray-100" /></div>
                      <span className="text-xs font-medium text-[#D4A910]">{u.completion_pct || 0}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <span className="text-xs text-gray-400">Page {page} of {totalPages}</span>
            <div className="flex gap-1">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 transition-colors" data-testid="prev-page"><ChevronLeft className="w-4 h-4 text-gray-500" /></button>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 transition-colors" data-testid="next-page"><ChevronRight className="w-4 h-4 text-gray-500" /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
